import { REWARD_LEVELS, type AppStrings } from '@veylme/shared';
import {
  Caption,
  Cell,
  Chip,
  List,
  Placeholder,
  Progress,
  Section,
  Title,
} from '@telegram-apps/telegram-ui';

import { RewardsSkeleton } from '../components/Skeleton.js';
import { BADGE_ICONS, useRewards } from '../hooks/useRewards.js';
import { useT } from '../hooks/useT.js';
import styles from './RewardsScreen.module.css';

/** Название бейджа по ключу через словарь текущего языка (fallback — сырой ключ). */
function badgeLabel(t: AppStrings, badge: string): string {
  switch (badge) {
    case 'viral_starter':
      return t.badge_viral_starter;
    case 'storyteller':
      return t.badge_storyteller;
    case 'loop_master':
      return t.badge_loop_master;
    default:
      return badge;
  }
}

/** Название темы/формата по ключу через словарь текущего языка (fallback — сырой ключ). */
function themeLabel(t: AppStrings, theme: string): string {
  switch (theme) {
    case 'confession':
      return t.theme_confession;
    case 'guesswho':
      return t.theme_guesswho;
    default:
      return theme;
  }
}

/**
 * Экран «Мои награды» (Веха 9 M3).
 *
 * Показывает уровень, заработанные бейджи, разблокированные форматы,
 * прогресс-бар до следующего уровня.
 *
 * Веха 13: Section/Cell для структуры, kit Progress для прогресс-бара (снимает
 * inline-стиль width). Логика не изменена. Экран-вкладка нижней навигации —
 * нативный BackButton не используется (переход через Tabbar).
 */
export function RewardsScreen() {
  const t = useT();
  const state = useRewards();

  return (
    <List>
      {state.status === 'loading' && <RewardsSkeleton />}

      {state.status === 'error' && (
        <Placeholder description={t.rw_error} />
      )}

      {state.status === 'ok' && (() => {
        const { level, badges, themes, confirmedReferrals, nextLevelAt } = state.data;
        const progressValue = nextLevelAt != null
          ? Math.round(Math.min(confirmedReferrals / nextLevelAt, 1) * 100)
          : 100;
        const levelIcon = level === 0 ? '💬' : level === 1 ? '🔥' : level === 2 ? '📖' : level === 3 ? '⭐' : '🏆';

        return (
          <>
            {/* Уровень */}
            <Section header={t.rw_title}>
              <div className={styles.levelCenter}>
                <div className={styles.levelIcon}>{levelIcon}</div>
                <Title level="3" weight="2" className={styles.levelNum}>
                  {t.rw_level(level)}
                </Title>
                <Caption Component="div" className={styles.levelRefs}>
                  {t.rw_confirmed_referrals(confirmedReferrals)}
                </Caption>
              </div>

              {/* Прогресс-бар через kit Progress (снимает inline-стиль width) */}
              {nextLevelAt != null && (
                <div className={styles.progressWrap}>
                  <Progress value={progressValue} />
                  <div className={styles.progressLabels}>
                    <Caption Component="span">{confirmedReferrals}</Caption>
                    <Caption Component="span">{t.rw_to_level(level + 1, nextLevelAt)}</Caption>
                  </div>
                </div>
              )}

              {nextLevelAt == null && (
                <Caption Component="div" className={styles.maxLevel}>
                  {t.rw_max_level}
                </Caption>
              )}
            </Section>

            {/* Бейджи */}
            {badges.length > 0 && (
              <Section header={t.rw_badges_section}>
                {badges.map((badge) => (
                  <Cell
                    key={badge}
                    before={
                      <span className={styles.badgeIcon}>
                        {BADGE_ICONS[badge] ?? '🎖️'}
                      </span>
                    }
                  >
                    {badgeLabel(t, badge)}
                  </Cell>
                ))}
              </Section>
            )}

            {/* Разблокированные форматы */}
            {themes.length > 0 && (
              <Section header={t.rw_formats_section}>
                <div className={styles.themeList}>
                  {themes.map((theme) => (
                    <Chip key={theme} mode="mono">
                      {themeLabel(t, theme)}
                    </Chip>
                  ))}
                </div>
              </Section>
            )}

            {/* Следующая награда */}
            {nextLevelAt != null && (() => {
              const nextCfg = REWARD_LEVELS.find((l) => l.level === level + 1);
              if (!nextCfg) return null;
              return (
                <Section header={t.rw_next_level_section(nextLevelAt)}>
                  <Caption Component="div" className={styles.nextInfo}>
                    {nextCfg.badges.map((b) => `${BADGE_ICONS[b]} ${badgeLabel(t, b)}`).join(', ')}
                    {nextCfg.themes.length > 0 && nextCfg.badges.length > 0 ? ' + ' : ''}
                    {nextCfg.themes.map((th) => t.rw_format_named(themeLabel(t, th))).join(', ')}
                  </Caption>
                </Section>
              );
            })()}
          </>
        );
      })()}
    </List>
  );
}
